/**
 * `EmptyWorkspaceScreen` — GVW-161 standalone state page.
 *
 * Empty workspace state when org has no layers. Lives at `/workspace/empty`.
 * Per copy-deck.md gvw.161.* and brief.md cross-app pivot to converter.
 *
 * Cite: design/30-engineering/ui-implementation/GVW-161/brief.md
 */

import { useEffect } from 'react';
import { tokens } from '@/design';
import { useLiveRegion } from '@/state/liveRegion';

export function EmptyWorkspaceScreen() {
  const liveRegion = useLiveRegion();

  useEffect(() => {
    // Copy key: gvw.161.title from copy-deck.md
    document.title = 'No layers in this organization · GeoViewer';
    liveRegion.announce('status', 'No layers in this organization');
  }, [liveRegion]);

  return (
    <main
      role="main"
      aria-labelledby="empty-workspace-heading"
      data-gvw-id="GVW-161"
      data-route="/workspace/empty"
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: tokens.color.surface[0],
        color: tokens.color.ink.primary,
        fontFamily: tokens.font.family.ui,
        padding: tokens.space[5],
        boxSizing: 'border-box',
      }}
    >
      <section
        style={{
          maxWidth: tokens.layout.standaloneStateExtraWide,
          width: '100%',
          background: tokens.color.surface[1],
          border: tokens.border.thin,
          borderRadius: tokens.radius.lg,
          padding: `${tokens.space[6]}px ${tokens.space[5]}px`,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: tokens.space[4],
          textAlign: 'center',
        }}
      >
        {/* Hero glyph per brief.md icon plan */}
        <div
          aria-hidden="true"
          style={{
            fontSize: 32,
            color: tokens.color.ink.secondary,
            fontFamily: 'Material Symbols Outlined',
          }}
        >
          layers
        </div>

        {/* Copy key: gvw.161.title */}
        <h1
          id="empty-workspace-heading"
          style={{
            margin: 0,
            fontSize: tokens.font.size['2xl'],
            lineHeight: tokens.font.lineHeight.tight,
            fontWeight: tokens.font.weight.semibold,
            color: tokens.color.ink.primary,
          }}
        >
          No layers in this organization
        </h1>

        {/* Copy key: gvw.161.body */}
        <p
          style={{
            margin: 0,
            fontSize: tokens.font.size.md,
            lineHeight: tokens.font.lineHeight.normal,
            color: tokens.color.ink.secondary,
            maxWidth: '42ch',
          }}
        >
          Upload data with the converter, then come back to add it as a layer.
        </p>

        {/* Recovery action per brief.md §10 cross-app to converter */}
        <button
          type="button"
          onClick={() => {
            // Cross-app to geoconverter per brief.md
            window.open('/geoconverter', '_blank');
          }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: tokens.space[2],
            padding: `${tokens.space[3]}px ${tokens.space[4]}px`,
            background: tokens.color.accent.primary,
            color: tokens.color.accent.on,
            border: 'none',
            borderRadius: tokens.radius.md,
            fontSize: tokens.font.size.md,
            fontWeight: tokens.font.weight.medium,
            fontFamily: 'inherit',
            cursor: 'pointer',
            textDecoration: 'none',
          }}
        >
          <span
            aria-hidden="true"
            style={{
              fontFamily: 'Material Symbols Outlined',
              fontSize: 18,
            }}
          >
            cloud_upload
          </span>
          Open the converter
        </button>
      </section>
    </main>
  );
}